import { useEffect, useRef, useState } from "react";
import { ds, SearchField } from "@/shared/components/ds";
import type { LaunchOption } from "./models/dashboard";

/**
 * The searchable launch switcher the OP Launch Dashboard and the reports share (MPN-2230,
 * MPN-2275). The parent passes the pool already narrowed by its filters; this owns only
 * whether it is open and what has been typed. Matches on the label or the id.
 */
export function LaunchPicker({ launches, value, onPick, filtered = false, width = 380, openRequest = 0, loading = false, label = "Launch" }: {
    launches: LaunchOption[];
    value: string;
    onPick: (id: string) => void;
    /** Whether the parent's filters narrow the pool: the count turns indigo so a filter change has visible feedback. */
    filtered?: boolean;
    width?: number | string;
    /** Bump to open the picker from outside (the empty state's "Choose a Launch"). */
    openRequest?: number;
    loading?: boolean;
    label?: string;
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const root = useRef<HTMLDivElement>(null);
    const selected = launches.find((l) => String(l.id) === value);

    useEffect(() => { if (openRequest) setOpen(true); }, [openRequest]);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false); };
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
    }, [open]);

    const q = query.trim().toLowerCase();
    const pool = launches.filter((l) => !q || l.label.toLowerCase().includes(q) || String(l.id).includes(q));
    const n = launches.length;

    return (
        <div ref={root} style={{ position: "relative", fontFamily: "var(--font-ui)", width, maxWidth: "100%" }}>
            <button type="button" aria-label={label} aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen(!open)}
                style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", boxSizing: "border-box", padding: "6px 10px",
                    border: "1px solid var(--border-control)", borderRadius: "var(--radius-md)", background: "#fff", fontFamily: "var(--font-ui)", cursor: "pointer", textAlign: "left" }}>
                <i className="bi bi-search" style={{ fontSize: 12, color: "var(--slate-400)" }} aria-hidden="true" />
                <span style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1, lineHeight: 1.25 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: "var(--fs-micro)", fontWeight: 700, letterSpacing: "var(--ls-caps)", textTransform: "uppercase", color: "var(--slate-400)" }}>{label}</span>
                        <span style={{ marginLeft: "auto", fontSize: "var(--fs-caption)", fontWeight: 600, color: filtered ? "var(--indigo-700)" : "var(--slate-400)", fontVariantNumeric: "tabular-nums" }}>
                            {loading ? "Loading…" : `${n.toLocaleString("en-US")} ${n === 1 ? "launch matches" : "launches match"}`}
                        </span>
                    </span>
                    <span style={{ fontSize: "var(--fs-cell)", fontWeight: 600, color: selected ? "var(--slate-950)" : "var(--slate-350)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {selected ? `${selected.label}  |  ${selected.id}` : value ? `Launch ${value}` : "Pick a launch"}
                    </span>
                </span>
                <i className="bi bi-chevron-down" style={{ fontSize: 11, color: "var(--slate-400)" }} aria-hidden="true" />
            </button>
            {open && (
                <div className={ds.pop} style={{ width: 460, maxWidth: "calc(100vw - 48px)", zIndex: 50 }} role="dialog">
                    <div style={{ padding: 10, borderBottom: "1px solid var(--border-rule)", display: "flex", flexDirection: "column", gap: 6 }}>
                        <SearchField value={query} onChange={setQuery} placeholder="Search name, ID or affiliate, e.g. Atlantic Cape CC" width="100%" label="Search launches" />
                        <span style={{ fontSize: "var(--fs-caption)", color: "var(--slate-500)" }}>
                            {pool.length.toLocaleString("en-US")} {pool.length === 1 ? "launch matches" : "launches match"}{filtered || q ? " your filters" : ""}
                        </span>
                    </div>
                    <div style={{ maxHeight: 330, overflowY: "auto", padding: 4 }} role="listbox" aria-label={label}>
                        {pool.map((l) => (
                            <button key={l.id} type="button" role="option" aria-selected={String(l.id) === value}
                                className={ds.popOption} style={{ background: String(l.id) === value ? "var(--indigo-50)" : undefined }}
                                onClick={() => { setOpen(false); setQuery(""); onPick(String(l.id)); }}>
                                <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flex: 1 }}>
                                    <span style={{ fontWeight: 600, color: "var(--slate-950)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.label}</span>
                                    <span style={{ fontSize: "var(--fs-small)", color: "var(--slate-500)", fontVariantNumeric: "tabular-nums" }}>ID {l.id}</span>
                                </span>
                            </button>
                        ))}
                        {pool.length === 0 && <div className={ds.popNone}>No launches match. Try another search or clear the filters.</div>}
                    </div>
                </div>
            )}
        </div>
    );
}
